/**
 * 「场景集合运维」页（Sidebar「系统」分组，路由 ``/scene-collection``）：
 * 集合 / 目录的锁定与隐藏管理。
 *
 * - 锁定：节点（含子树）禁删 / 禁改 / 禁加场景；语义沿祖先链向下传递。
 * - 隐藏：默认前台「场景管理」列表不展示；持权限者通过 ``includeHidden=true`` 旁路可见。
 *
 * 权限：``menu:scene-collection``。菜单可见性由 Sidebar 同权限点决定，路由层
 * ``<RequirePermission>`` 兜底防 URL 直填，后端 ``/scene-tree/lock|hide`` 同码硬门。
 * 批量发起预览相关交互仍在后台管理页的「场景预览」Tab（``Admin/ScenePreviewTab.tsx``）。
 *
 * 交互约定（两条都是刻意为之，改动前请先读这段）：
 * 1. 目录默认**全部收起**（受控 ``expandedKeys``，不用 ``defaultExpandAll``）。运维库里
 *    集合层级深、叶子多，全展开时首屏是几百行，找不到要操作的节点。
 * 2. 锁定 / 隐藏切换**不重拉整棵树**，只就地改那一个节点的字段（``patchNode``）。
 *    早期实现每次切换都 ``loadTree()``，树被整体重建 → 展开态与滚动位置全部回到初始，
 *    用户点一下按钮眼前的内容就全变了。整树重拉只保留给「刷新」按钮和切换
 *    「显示隐藏节点」这两个用户主动发起、预期会变的动作。
 * 3. 「清理匿名集合」按钮只对 **admin 角色**渲染（``useCurrentUser().hasRole('admin')``，
 *    不是本页的 ``menu:scene-collection``）。清理是不可逆删除，且被删的匿名叶子
 *    正被历史 Job 的 ``sceneSetIds`` 引用（叶子没了那些 Job 重跑不出场景），
 *    权限面必须比页面其余可逆开关更窄；后端 ``require_role("admin")`` 是硬门，
 *    前端这层只是不给非 admin 看见入口。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, App, Button, Card, Space, Spin, Switch, Tag, Tree, Typography,
} from 'antd';
import {
  DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, FileOutlined,
  FolderOutlined, LockOutlined, ReloadOutlined, UnlockOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';

import {
  getSceneTree, purgeAnonymousSceneSets, SceneTreeNode,
  setSceneTreeNodeHidden, setSceneTreeNodeLocked,
} from '@apis/simulation';
import { useCurrentUser } from '@/common/auth/adminAcl';

/** 匿名集合保留期(天)，与后端 ``JobService.ANON_LEAF_RETENTION_DAYS`` 对齐。 */
const ANON_RETENTION_DAYS = 90;

const { Title, Text } = Typography;


export default function SceneCollectionPage() {
  const { message, modal } = App.useApp();
  const { hasRole } = useCurrentUser();
  const [includeHidden, setIncludeHidden] = useState(true); // admin 视角默认看全
  const [treeData, setTreeData] = useState<SceneTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<Set<number>>(new Set());
  // 受控展开态：初始空数组 = 全部收起。整树重拉（刷新 / 切 includeHidden）时
  // 一并清空，避免残留已不存在节点的 key。
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [purging, setPurging] = useState(false);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getSceneTree(includeHidden);
      setTreeData(t);
      setExpandedKeys([]);
    } catch (e) {
      message.error('加载场景树失败');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [includeHidden, message]);

  useEffect(() => { loadTree(); }, [loadTree]);

  /** 清理匿名集合：先 dryRun 报数 → 用户确认 → 真删 → 重拉整树。
   *
   * 这里**故意**走整树重拉而不是就地摘节点：一次可能删掉成百上千个叶子，
   * 且删完容器多半空了，前端逐个 patch 既啰嗦又容易和后端口径漂移。这是用户
   * 主动发起、预期树会变的动作，重拉不违反本页「不要让数突然全变了」的约定。
   */
  const handlePurgeAnon = useCallback(async () => {
    setPurging(true);
    try {
      const preview = await purgeAnonymousSceneSets({
        retentionDays: ANON_RETENTION_DAYS, dryRun: true,
      });
      if (!preview.deletedNodes) {
        message.info(`没有超过 ${ANON_RETENTION_DAYS} 天的匿名集合，无需清理`);
        return;
      }
      // antd 的 modal.confirm 返回的是实例句柄而非 Promise<boolean>,
      // 包一层 Promise 把 onOk / onCancel 转成可 await 的布尔值。
      const confirmed = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: '清理匿名集合',
          okText: `确认删除 ${preview.deletedNodes} 个`,
          okButtonProps: { danger: true },
          cancelText: '取消',
          content: (
            <div>
              <p>
                将删除 <b>{preview.deletedNodes}</b> 个创建于{' '}
                {preview.cutoff.slice(0, 10)} 之前的匿名集合
                （「按任务查询」自动生成的 <code>bytaskJ*</code> 叶子），
                连带 <b>{preview.deletedLinks}</b> 条场景关联。
              </p>
              <p style={{ color: '#cf1322', marginBottom: 0 }}>
                删除不可逆。引用这些集合的历史 Job 将无法再重跑
                （查不到对应场景）。普通场景集合不受影响。
              </p>
            </div>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
      const result = await purgeAnonymousSceneSets({
        retentionDays: ANON_RETENTION_DAYS,
      });
      message.success(
        `已清理 ${result.deletedNodes} 个匿名集合、${result.deletedLinks} 条场景关联`,
      );
      loadTree();
    } catch (e) {
      message.error('清理匿名集合失败');
      console.error(e);
    } finally {
      setPurging(false);
    }
  }, [message, modal, loadTree]);

  /** 就地改一个节点的字段，保持树结构与其余节点的引用不变（不触发整棵树重建）。 */
  const patchNode = useCallback((id: number, patch: Partial<SceneTreeNode>) => {
    const walk = (nodes: SceneTreeNode[]): SceneTreeNode[] =>
      nodes.map((n) => {
        if (n.id === id) return { ...n, ...patch };
        if (n.children?.length) {
          const kids = walk(n.children);
          return kids === n.children ? n : { ...n, children: kids };
        }
        return n;
      });
    setTreeData((prev) => walk(prev));
  }, []);

  /** 从树里摘掉一个节点（隐藏后在「不显示隐藏节点」视图下它本就不该还在）。 */
  const dropNode = useCallback((id: number) => {
    const walk = (nodes: SceneTreeNode[]): SceneTreeNode[] =>
      nodes.filter((n) => n.id !== id).map((n) => (
        n.children?.length ? { ...n, children: walk(n.children) } : n
      ));
    setTreeData((prev) => walk(prev));
  }, []);

  const setBusy = (id: number, busy: boolean) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleToggleLock = useCallback(
    async (n: SceneTreeNode) => {
      setBusy(n.id, true);
      try {
        await setSceneTreeNodeLocked(n.id, !n.isLocked);
        message.success(`节点「${n.name}」已${!n.isLocked ? '锁定' : '解锁'}`);
        // 只改这一个节点：锁定语义虽沿祖先链向下传递，但子节点自身的 isLocked
        // 字段并不会跟着变（后端只写当前行），所以无需重拉整棵树。
        patchNode(n.id, { isLocked: !n.isLocked });
      } catch (e) {
        message.error('操作失败');
        console.error(e);
      } finally {
        setBusy(n.id, false);
      }
    },
    [message, patchNode],
  );

  const handleToggleHidden = useCallback(
    async (n: SceneTreeNode) => {
      setBusy(n.id, true);
      try {
        const nextHidden = !n.isHidden;
        await setSceneTreeNodeHidden(n.id, nextHidden);
        message.success(`节点「${n.name}」已${nextHidden ? '隐藏' : '取消隐藏'}`);
        if (nextHidden && !includeHidden) {
          // 「不显示隐藏节点」视图下刚被隐藏的节点应当消失,否则界面与开关语义矛盾
          dropNode(n.id);
        } else {
          patchNode(n.id, { isHidden: nextHidden });
        }
      } catch (e) {
        message.error('操作失败');
        console.error(e);
      } finally {
        setBusy(n.id, false);
      }
    },
    [message, includeHidden, patchNode, dropNode],
  );

  // 「全部展开」用：所有目录节点（nodeFlag !== 1）的 key。叶子无子树，不必入列。
  const allDirKeys = useMemo<React.Key[]>(() => {
    const keys: React.Key[] = [];
    const walk = (nodes: SceneTreeNode[]) => nodes.forEach((n) => {
      if (n.children?.length) {
        keys.push(n.id);
        walk(n.children);
      }
    });
    walk(treeData);
    return keys;
  }, [treeData]);

  const antTreeData = useMemo<DataNode[]>(() => {
    const convert = (nodes: SceneTreeNode[]): DataNode[] =>
      nodes.map((n) => {
        const busy = pending.has(n.id);
        return {
          key: n.id,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {n.nodeFlag === 1
                  ? <FileOutlined style={{ marginRight: 4, color: '#999' }} />
                  : <FolderOutlined style={{ marginRight: 4, color: '#faad14' }} />}
                {n.name}
                {n.isLocked && (
                  <Tag color="orange" style={{ marginLeft: 8 }} icon={<LockOutlined />}>
                    锁定
                  </Tag>
                )}
                {n.isHidden && (
                  <Tag color="default" style={{ marginLeft: 4 }} icon={<EyeInvisibleOutlined />}>
                    隐藏
                  </Tag>
                )}
              </span>
              <Space size={6}>
                <Button
                  size="small"
                  type={n.isLocked ? 'primary' : 'default'}
                  icon={n.isLocked ? <UnlockOutlined /> : <LockOutlined />}
                  loading={busy}
                  onClick={(e) => { e.stopPropagation(); handleToggleLock(n); }}
                >
                  {n.isLocked ? '解锁' : '锁定'}
                </Button>
                <Button
                  size="small"
                  icon={n.isHidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  loading={busy}
                  onClick={(e) => { e.stopPropagation(); handleToggleHidden(n); }}
                >
                  {n.isHidden ? '取消隐藏' : '隐藏'}
                </Button>
              </Space>
            </div>
          ),
          children: n.children?.length ? convert(n.children) : undefined,
          isLeaf: n.nodeFlag === 1,
        };
      });
    return convert(treeData);
  }, [treeData, pending, handleToggleLock, handleToggleHidden]);

  return (
    <div style={{ padding: 16, background: '#fff', minHeight: '100%' }}>
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Title level={4} style={{ marginTop: 0 }}>场景集合运维</Title>
        <Text type="secondary">
          对场景集 / 目录设置「锁定」（禁删 / 禁改 / 禁加场景）或「隐藏」（前台不展示）。
          切换会立即生效，不影响下游正在运行的 Job。批量发起预览请到「后台管理 → 场景预览」。
        </Text>
      </div>

      <Card
        title="集合 · 锁定 / 隐藏"
        extra={
          <Space>
            <Button
              size="small"
              onClick={() => setExpandedKeys(
                expandedKeys.length ? [] : allDirKeys,
              )}
              disabled={!allDirKeys.length}
            >
              {expandedKeys.length ? '全部收起' : '全部展开'}
            </Button>
            <Text type="secondary">显示隐藏节点</Text>
            <Switch checked={includeHidden} onChange={setIncludeHidden} />
            <Button icon={<ReloadOutlined />} onClick={loadTree} loading={loading}>
              刷新
            </Button>
            {hasRole('admin') && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handlePurgeAnon}
                loading={purging}
              >
                清理匿名集合
              </Button>
            )}
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          message="说明"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                <b>锁定</b>：节点（含子树）禁止做删 / 改 / 加场景操作；语义沿祖先链向下传递。
              </li>
              <li>
                <b>隐藏</b>：前台「场景管理」列表默认不展示该节点及其后代；后台 admin 可通过
                「显示隐藏节点」开关或 <code>includeHidden=true</code> 看到。
              </li>
            </ul>
          }
          style={{ marginBottom: 12 }}
        />
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <Tree
            treeData={antTreeData}
            blockNode
            showLine={{ showLeafIcon: false }}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
          />
        )}
      </Card>
    </Space>
    </div>
  );
}
